import { ChangeDetectionStrategy, Component, CUSTOM_ELEMENTS_SCHEMA, effect, inject, signal, ViewEncapsulation } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { fieldTypeRaw, StructureField } from '../../interfaces';
import { DragHandleComponent } from "src/app/shared/drag-handle/drag-handle.component";
import { SingleResponseFieldWrapperComponent, TextFieldWrapperComponent, MultipleResponseFieldWrapperComponent, LevelSatisfactionFieldWrapperComponent } from "./components";
import { IncidentsReporterSurvey, IncidentsReporterSurveyForm, IncidentsReporterSurveyFormFactory } from '../../services';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Snackbar } from 'src/app/services';

@Component({
  selector: 'app-incidents-reporter-survey',
  standalone: true,
  imports: [
    TranslateModule,
    ReactiveFormsModule,
    FormsModule,
    DragHandleComponent,
    SingleResponseFieldWrapperComponent,
    TextFieldWrapperComponent,
    MultipleResponseFieldWrapperComponent,
    LevelSatisfactionFieldWrapperComponent
],
  templateUrl: './incidents-reporter-survey.component.html',
  styleUrls: ['./incidents-reporter-survey.component.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class IncidentsReporterSurveyComponent  {
  // Service injections
  private _incidentsReporterSurveyService = inject(IncidentsReporterSurvey);
  private _incidentsReporterSurveyForm = inject(IncidentsReporterSurveyForm);
  private _incidentsReporterSurveyFormFactory = inject(IncidentsReporterSurveyFormFactory);
  private _translateService = inject(TranslateService); // TODO: EN SSR SELECTED LANGUAGE SERVICE
  private _snackbarService = inject(Snackbar);
  
  // Service signal states
  protected surveyData = this._incidentsReporterSurveyService.surveyData;
  
  // Local signals
  protected fieldList = signal<StructureField[]>([]);

  // Local properties
  protected currentLang = this._translateService.currentLang || this._translateService.getDefaultLang() || 'es';
  
  protected typeText = fieldTypeRaw.text;
  protected typeTextarea = fieldTypeRaw.textarea;
  protected typeSelectionUnique = fieldTypeRaw.selection_unique;
  protected typeSelectionMulti = fieldTypeRaw.selection_multi;
  protected typeSatisfactionLevel3 = fieldTypeRaw.satisfaction_level_3;
  protected typeSatisfactionLevel5 = fieldTypeRaw.satisfaction_level_5;
  protected typeEmail = fieldTypeRaw.email;
  
  // Form
  public surveyForm = signal<FormGroup | null>(null);
  
  // Effects
  setFieldListAndFormGroup = effect(() => {
    if (this.surveyData()) {
      this.fieldList.set(this.surveyData().structure[this.currentLang] || []);
    }
    
    if(!this.fieldList() || this.fieldList().length === 0 ) return;
    
    this.surveyForm.set(this._incidentsReporterSurveyFormFactory.createForm(this.fieldList()));
  }, { allowSignalWrites: true });
  
  // Methods
  protected isLastField(index: number): boolean {
    return index === this.fieldList().length - 1;
  }
  
  protected toggleBottomSheet(): void {
    this._incidentsReporterSurveyForm.toggleBottomSheet();
  }
  
  protected onSubmit() {
    this._incidentsReporterSurveyForm.setMarkAllFormFieldsAsTouched(true);
    
    if (!this.surveyForm()) return;
    
    if (!this.surveyForm()?.valid) {
      const errorMessage = this._translateService.instant('No se ha enviado la encuesta. Por favor, revise los campos');
      this._snackbarService.showErrorSnackbar(errorMessage);
      return;
    }
    
    const surveyId = this.surveyData()?.id;
    if (!surveyId) return;
    
    this._incidentsReporterSurveyForm.submitForm(this.surveyForm(), surveyId, this.fieldList());
      
    this._incidentsReporterSurveyFormFactory.resetForm(this.surveyForm());
  }
}
